import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { createContext, type ReactNode, useContext, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

export type MessageShiftFootnoteShift = {
    readonly shift_id: string;
    readonly starts_at: string;
    readonly ends_at: string;
    readonly status: string;
};

export type MessageShiftFootnote = {
    readonly number: number | null;
    readonly outreach_id: string | null;
    readonly client_name: string | null;
    readonly shifts: ReadonlyArray<MessageShiftFootnoteShift>;
};

export type OpenShiftFootnoteShift = MessageShiftFootnoteShift & {
    readonly client_name: string | null;
};

const ShiftFootnoteOpenContext = createContext<
    ((shift: OpenShiftFootnoteShift) => void) | null
>(null);

export function ShiftFootnoteOpenProvider({
    children,
    onOpenShift,
}: {
    readonly children: ReactNode;
    readonly onOpenShift: (shift: OpenShiftFootnoteShift) => void;
}) {
    return (
        <ShiftFootnoteOpenContext.Provider value={onOpenShift}>
            {children}
        </ShiftFootnoteOpenContext.Provider>
    );
}

const COLLAPSED_FOOTNOTE_COUNT = 4;

const STATUS_LABELS: Readonly<Record<string, string>> = {
    open: 'Open',
    needs_coverage: 'Open',
    assigned: 'Filled',
    pending_confirmation: 'Filled',
    assigned_and_confirmed: 'Confirmed',
    in_progress: 'In progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

const OPEN_STATUSES = new Set(['open', 'needs_coverage']);

function formatShiftDay(iso: string, timezone: string | undefined): string {
    return new Date(iso).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        ...(timezone && { timeZone: timezone }),
    });
}

function formatShiftTime(iso: string, timezone: string | undefined): string {
    return new Date(iso).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...(timezone && { timeZone: timezone }),
    });
}

function footnoteSummary(
    footnote: MessageShiftFootnote,
    shift: MessageShiftFootnoteShift,
    timezone: string | undefined,
): string {
    const day = formatShiftDay(shift.starts_at, timezone);
    const time = `${formatShiftTime(shift.starts_at, timezone)}–${formatShiftTime(shift.ends_at, timezone)}`;
    const client = footnote.client_name ? ` · ${footnote.client_name}` : '';
    return `${day} · ${time}${client}`;
}

function FootnoteStatusChip({ status }: { readonly status: string }) {
    const label = STATUS_LABELS[status];
    if (!label) {
        return null;
    }
    return (
        <span
            className={cn(
                'rounded-full px-1.5 py-px text-[10px] leading-4 font-medium',
                OPEN_STATUSES.has(status)
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                    : 'bg-muted text-muted-foreground',
            )}
        >
            {label}
        </span>
    );
}

function ShiftFootnoteLink({
    footnote,
    shift,
    timezone,
    onSelect,
}: {
    readonly onSelect: () => void;
    readonly footnote: MessageShiftFootnote;
    readonly shift: MessageShiftFootnoteShift;
    readonly timezone?: string;
}) {
    const onOpenShift = useContext(ShiftFootnoteOpenContext);
    const isConfirmation = footnote.number === null;
    // Every row is a target when the surface can open a shift in place --
    // including a confirmation, whose one shift is the most obvious thing to
    // want. Underline follows clickability rather than the row's kind.
    const isClickable = onOpenShift !== null || Boolean(footnote.outreach_id);
    const summary = footnoteSummary(footnote, shift, timezone);
    const rowContent = (
        <span className="flex min-w-0 items-center gap-1.5">
            {footnote.number !== null ? (
                <span className="bg-muted text-muted-foreground rounded px-1 font-mono text-[10px] leading-4 tabular-nums">
                    {footnote.number}
                </span>
            ) : null}
            <span
                className={cn(
                    'whitespace-normal text-left',
                    isClickable && 'underline underline-offset-2',
                )}
            >
                {summary}
            </span>
            <FootnoteStatusChip status={shift.status} />
        </span>
    );
    const rowClassName =
        'text-foreground hover:bg-muted flex max-w-full items-center rounded-sm px-0.5 text-xs transition-colors';

    // An in-place overlay beats navigating away: the coordinator keeps the
    // thread they are reading. This wins for every row that has one --
    // numbered, outreach-linked, or a confirmation -- because the shift is
    // what the row names. Surfaces without overlay context (the outreach
    // page, the activity feed) still fall through to the outreach link.
    if (onOpenShift) {
        return (
            <button
                className={rowClassName}
                data-testid="message-shift-footnote-link"
                onClick={() => {
                    onSelect();
                    onOpenShift({
                        ...shift,
                        client_name: footnote.client_name,
                    });
                }}
                type="button"
            >
                {rowContent}
            </button>
        );
    }

    if (isConfirmation) {
        return (
            <span className="text-foreground flex max-w-full items-center px-0.5 text-xs">
                {rowContent}
            </span>
        );
    }

    if (footnote.outreach_id) {
        return (
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <Link
                        className={rowClassName}
                        data-testid="message-shift-footnote-link"
                        params={{ 'callout-id': footnote.outreach_id }}
                        to="/outreaches/$callout-id"
                    >
                        {rowContent}
                    </Link>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs" side="top">
                    Open outreach
                </TooltipContent>
            </Tooltip>
        );
    }

    return <span className={rowClassName}>{rowContent}</span>;
}

export function MessageShiftFootnotes({
    footnotes,
    onClose,
    timezone,
}: {
    readonly footnotes: ReadonlyArray<MessageShiftFootnote>;
    readonly onClose: () => void;
    readonly timezone?: string;
}) {
    const [showAll, setShowAll] = useState(false);
    if (footnotes.length === 0) {
        return null;
    }
    const visibleFootnotes = showAll
        ? footnotes
        : footnotes.slice(0, COLLAPSED_FOOTNOTE_COUNT);
    const hiddenCount = footnotes.length - visibleFootnotes.length;
    const rows = visibleFootnotes.flatMap((footnote) =>
        footnote.shifts.map((shift) => ({ footnote, shift })),
    );

    return (
        <div
            aria-label="Shifts in this message"
            className="bg-popover text-popover-foreground mt-2 flex max-h-80 flex-col gap-2 overflow-y-auto rounded-xl border p-3 shadow-sm"
            data-testid="message-shift-footnotes"
        >
            <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
                    Shifts in this message
                </span>
                <Button
                    aria-label="Close shifts"
                    onClick={onClose}
                    size="icon"
                    variant="ghost"
                    className="size-6"
                >
                    <X className="size-3.5" />
                </Button>
            </div>
            {rows.map(({ footnote, shift }) => (
                <ShiftFootnoteLink
                    footnote={footnote}
                    onSelect={onClose}
                    key={shift.shift_id}
                    shift={shift}
                    timezone={timezone}
                />
            ))}
            {hiddenCount > 0 ? (
                <button
                    className="text-muted-foreground hover:text-foreground self-start text-[11px] underline underline-offset-2"
                    onClick={() => setShowAll(true)}
                    type="button"
                >
                    Show all {footnotes.length} shifts
                </button>
            ) : null}
        </div>
    );
}
